import { useTranslation } from 'react-i18next';

import { NumberInput, TextInput } from '@any-ui-react/core';
import { SingleDatePicker } from '@any-ui-react/dates';
import { AsyncSelect, Select } from '@any-ui-react/select';

import { InputPrice, RangeDatePicker } from '@anyx/shared-ui';

import { Field } from '../../types';
import { DefaultOperator } from '../operator-selector';

import {
  useAsyncSelectEditor,
  useDateEditor,
  useInputPriceEditor,
  useMultiSelectEditor,
  useNumberEditor,
  useSelectEditor,
  useTextEditor,
  useValueEditor,
} from './hooks';

export interface ValueEditorProps {
  field: Field;
  operator: string;
  values?: string[];
  onChange?: (values: string[]) => void;
  errors?: (string | undefined)[];
  compact?: boolean;
  disabled?: boolean;
}

export const NO_VALUE_EDITOR: string[] = [DefaultOperator.enum.NULL, DefaultOperator.enum.NOT_NULL];

const DOUBLE_VALUE_EDITOR: string[] = [
  DefaultOperator.enum.BETWEEN,
  DefaultOperator.enum.NOT_BETWEEN,
];

export const ValueEditor = ({
  field,
  operator,
  values,
  onChange,
  errors,
  compact,
  disabled = false,
}: ValueEditorProps) => {
  const { t } = useTranslation('shared');
  const { baseProps, valueEditorType, getErrorMessage, ...valueEditorProps } = useValueEditor({
    compact,
    field,
    values,
    operator,
    onChange,
    errors,
  });

  const commonEditorInputProps = { ...valueEditorProps, field, values };

  const { handleTextEditorValueChange, getTextEditorValue, ...textEditorProps } =
    useTextEditor(commonEditorInputProps);

  const { handleNumberEditorValueChange, getNumberEditorValue, ...numberEditorProps } =
    useNumberEditor(commonEditorInputProps);

  const { handleSelectEditorValueChange, getSelectEditorValue, ...selectEditorProps } =
    useSelectEditor(commonEditorInputProps);

  const {
    handleMultiSelectEditorValueChange,
    getMultiSelectEditorValue,
    ...multiSelectEditorProps
  } = useMultiSelectEditor(commonEditorInputProps);

  const { handleDateEditorValueChange, getDateEditorValue } = useDateEditor(commonEditorInputProps);

  const { handleInputPriceEditorValueChange, getInputPriceEditorValue, ...inputPriceEditorProps } =
    useInputPriceEditor(commonEditorInputProps);

  const {
    handleAsyncSelectEditorValueChange,
    getAsyncSelectEditorValue,
    ...asyncSelectEditorProps
  } = useAsyncSelectEditor(commonEditorInputProps);

  if (NO_VALUE_EDITOR.includes(operator)) {
    return null;
  }

  if (DOUBLE_VALUE_EDITOR.includes(operator)) {
    if (valueEditorType === 'date') {
      return (
        <RangeDatePicker
          {...baseProps}
          withDatesRangeSelect={false}
          error={getErrorMessage(0) || getErrorMessage(1)}
          value={[getDateEditorValue(0), getDateEditorValue(1)]}
          onChange={(date) => {
            handleDateEditorValueChange(0)(date[0]);
            handleDateEditorValueChange(1)(date[1]);
          }}
          disabled={disabled}
        />
      );
    }

    const editors = ['from', 'to'].map((key, i) => {
      if (valueEditorType === 'select') {
        return (
          <Select
            key={key}
            {...baseProps}
            {...selectEditorProps}
            value={getSelectEditorValue(i)}
            onChange={handleSelectEditorValueChange(i)}
            error={getErrorMessage(i)}
            wrapper={{ className: baseProps.className }}
            disabled={disabled}
          />
        );
      }

      if (valueEditorType === 'input-price') {
        return (
          <InputPrice
            key={key}
            {...baseProps}
            {...inputPriceEditorProps}
            value={getInputPriceEditorValue(i)}
            onChange={handleInputPriceEditorValueChange(i)}
            error={getErrorMessage(i)}
            disabled={disabled}
          />
        );
      }

      if (field.inputType === 'number') {
        return (
          <NumberInput
            key={key}
            {...baseProps}
            {...numberEditorProps}
            value={getNumberEditorValue(i)}
            onChange={(v) => handleNumberEditorValueChange(i)(v as number)}
            error={getErrorMessage(i)}
            hideControls
            disabled={disabled}
          />
        );
      }

      return (
        <TextInput
          key={key}
          {...baseProps}
          {...textEditorProps}
          value={getTextEditorValue(i)}
          onChange={handleTextEditorValueChange(i)}
          error={getErrorMessage(i)}
          disabled={disabled}
        />
      );
    });

    return (
      <div className="flex items-start gap-x-2">
        {editors[0]}
        <span className="flex h-8 items-center leading-none">~</span>
        {editors[1]}
      </div>
    );
  }

  if (field.valueEditorType === 'date') {
    return (
      <div className={baseProps.className}>
        <SingleDatePicker
          {...baseProps}
          placeholder={t('shared.field.selectDate.placeholder')}
          value={getDateEditorValue()}
          onChange={handleDateEditorValueChange()}
          error={getErrorMessage()}
          disabled={disabled}
        />
      </div>
    );
  }

  if (field?.valueEditorType === 'select') {
    return (
      <Select
        {...baseProps}
        {...selectEditorProps}
        value={getSelectEditorValue()}
        onChange={handleSelectEditorValueChange()}
        error={getErrorMessage()}
        wrapper={{ className: baseProps.className }}
        disabled={disabled}
      />
    );
  }

  if (field?.valueEditorType === 'multi-select') {
    return (
      <Select
        {...baseProps}
        {...multiSelectEditorProps}
        classNames={{
          valueContainer: () => 'flex-nowrap! [&_.option-label]:truncate',
          option: () => '[&_label]:truncate',
        }}
        value={getMultiSelectEditorValue()}
        onChange={handleMultiSelectEditorValueChange}
        error={getErrorMessage()}
        wrapper={{ className: baseProps.className }}
        disabled={disabled}
      />
    );
  }

  if (field.valueEditorType === 'async-select' && field.onLoadOptions) {
    const error = getErrorMessage();
    const value = getAsyncSelectEditorValue();
    return (
      <AsyncSelect
        {...baseProps}
        {...asyncSelectEditorProps}
        loadOptions={field.onLoadOptions(value)}
        defaultOptions
        value={value}
        onChange={handleAsyncSelectEditorValueChange()}
        error={error}
        wrapper={{ className: baseProps.className }}
        disabled={disabled}
      />
    );
  }

  if (field.valueEditorType === 'input-price') {
    return (
      <InputPrice
        {...baseProps}
        {...inputPriceEditorProps}
        value={getInputPriceEditorValue()}
        onChange={handleInputPriceEditorValueChange()}
        error={getErrorMessage()}
        disabled={disabled}
      />
    );
  }

  if (field.inputType === 'number') {
    return (
      <NumberInput
        {...baseProps}
        {...numberEditorProps}
        value={getNumberEditorValue()}
        onChange={(v) => handleNumberEditorValueChange()(v as number)}
        error={getErrorMessage()}
        hideControls
        disabled={disabled}
      />
    );
  }

  return (
    <TextInput
      {...baseProps}
      {...textEditorProps}
      value={getTextEditorValue()}
      onChange={handleTextEditorValueChange()}
      error={getErrorMessage()}
      disabled={disabled}
    />
  );
};
